import { createContext, useContext, ReactNode, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

// Lokalizacje z gminy Pobiedziska
export type AirQualityLocation = {
  id: string;
  name: string;
  city: string;
  searchTerms: string[]; // Terminy do wyszukiwania w nazwie szkoły lub mieście
};

export const AVAILABLE_LOCATIONS: AirQualityLocation[] = [
  {
    id: "pobiedziska",
    name: "Pobiedziska",
    city: "POBIEDZISKA",
    searchTerms: ["POBIEDZISKA", "KAZIMIERZA ODNOWICIELA"],
  },
  {
    id: "pobiedziska-letnisko",
    name: "Pobiedziska Letnisko",
    city: "POBIEDZISKA LETNISKO",
    searchTerms: ["POBIEDZISKA LETNISKO", "KONSTYTUCJI 3 MAJA"],
  },
  {
    id: "biskupice",
    name: "Biskupice",
    city: "BISKUPICE",
    searchTerms: ["BISKUPICE"],
  },
  {
    id: "jerzykowo",
    name: "Jerzykowo",
    city: "JERZYKOWO",
    searchTerms: ["JERZYKOWO"],
  },
];

export const DEFAULT_LOCATION_ID = "pobiedziska";

export type ProcessedAirQualityData = {
  aqi: number;
  pm25: number;
  pm10: number;
  humidity: number;
  pressure: number;
  temperature: number;
  location: string;
  stationName: string;
  lastUpdate: string;
  coordinates: {
    lat: number;
    lng: number;
  };
};

// URL API jakości powietrza
const AIR_QUALITY_API_URL = "https://public-esa.ose.gov.pl/api/v1/smog";

// Funkcja do uzyskania URL API z proxy (omija problemy z CORS)
function getAirQualityApiUrl(): string {
  // W development użyj lokalnego proxy (setupProxy.js)
  if (process.env.NODE_ENV === 'development') {
    return '/api/air-quality';
  }
  
  // W produkcji użyj publicznego CORS proxy
  return `https://api.allorigins.win/raw?url=${encodeURIComponent(AIR_QUALITY_API_URL)}`;
}

const CACHE_KEY = "airQualityData";
const CACHE_EXPIRATION_MS = 60 * 60 * 1000; // 1 godzina w milisekundach
const SELECTED_LOCATION_KEY = "airQualitySelectedLocation";

type School = {
  name: string;
  street: string | null;
  post_code: string;
  city: string;
  longitude: string;
  latitude: string;
};

type AirQualityDataPoint = {
  humidity_avg: number;
  pressure_avg: number;
  temperature_avg: number;
  pm10_avg: number;
  pm25_avg: number;
};

type SmogDataItem = {
  school: School;
  data: AirQualityDataPoint;
  timestamp: string;
};

type AirQualityApiResponse = {
  smog_data: SmogDataItem[];
  it_has_next_page: boolean;
  pages_total: number | null;
};

type CachedAirQualityData = {
  data: SmogDataItem[];
  timestamp: number;
};

// Funkcja do znajdowania stacji dla wybranej lokalizacji
function findStationForLocation(
  stations: SmogDataItem[],
  location: AirQualityLocation
): SmogDataItem | null {
  if (stations.length === 0) return null;

  // Szukaj stacji, która pasuje do terminów wyszukiwania
  for (const station of stations) {
    const schoolName = station.school.name.toUpperCase();
    const cityName = station.school.city.toUpperCase();
    
    // Sprawdź czy którykolwiek z terminów pasuje
    for (const term of location.searchTerms) {
      if (schoolName.includes(term.toUpperCase()) || cityName.includes(term.toUpperCase())) {
        return station;
      }
    }
  }

  // Jeśli nie znaleziono dokładnego dopasowania, spróbuj znaleźć po nazwie miasta
  const matchingStations = stations.filter(station => 
    station.school.city.toUpperCase().includes(location.city.toUpperCase())
  );

  if (matchingStations.length > 0) {
    return matchingStations[0];
  }

  return null;
}

// Funkcja do obliczania AQI na podstawie PM2.5 i PM10
function calculateAQI(pm25: number, pm10: number): number {
  const pm25Index = Math.min((pm25 / 25) * 50, 500);
  const pm10Index = Math.min((pm10 / 50) * 50, 500);
  return Math.round(Math.max(pm25Index, pm10Index));
}

// Funkcja do przetwarzania danych ze stacji
function processStationData(station: SmogDataItem): ProcessedAirQualityData {
  const { data, school, timestamp } = station;
  const aqi = calculateAQI(data.pm25_avg, data.pm10_avg);

  return {
    aqi,
    pm25: Math.round(data.pm25_avg * 100) / 100,
    pm10: Math.round(data.pm10_avg * 100) / 100,
    humidity: Math.round(data.humidity_avg * 100) / 100,
    pressure: Math.round(data.pressure_avg * 100) / 100,
    temperature: Math.round(data.temperature_avg * 100) / 100,
    location: `${school.city}${school.street ? `, ${school.street}` : ""}`,
    stationName: school.name,
    lastUpdate: timestamp,
    coordinates: {
      lat: parseFloat(school.latitude),
      lng: parseFloat(school.longitude),
    },
  };
}

// Funkcja sprawdzająca czy cache jest ważny
function isCacheValid(cachedData: CachedAirQualityData | null): boolean {
  if (!cachedData || !cachedData.timestamp) return false;
  return Date.now() - cachedData.timestamp < CACHE_EXPIRATION_MS;
}

// Funkcja pomocnicza do pobierania i walidacji zapisanej lokalizacji
function getSavedLocation(): AirQualityLocation {
  try {
    const savedLocationId = localStorage.getItem(SELECTED_LOCATION_KEY);
    if (savedLocationId) {
      const location = AVAILABLE_LOCATIONS.find(loc => loc.id === savedLocationId);
      if (location) {
        return location;
      }
    }
  } catch (error) {
    console.warn("Error reading saved location from localStorage:", error);
  }
  return AVAILABLE_LOCATIONS.find(loc => loc.id === DEFAULT_LOCATION_ID) || AVAILABLE_LOCATIONS[0];
}

// Funkcja do ładowania danych z cache
function loadFromCache(location: AirQualityLocation): ProcessedAirQualityData | null {
  try {
    const cachedDataString = localStorage.getItem(CACHE_KEY);
    if (cachedDataString) {
      const cachedData: CachedAirQualityData = JSON.parse(cachedDataString);
      if (cachedData.data && cachedData.data.length > 0) {
        const station = findStationForLocation(cachedData.data, location);
        if (station) {
          return processStationData(station);
        }
      }
    }
  } catch (error) {
    console.warn("Error loading from cache:", error);
  }
  return null;
}

type AirQualityContextType = {
  airQuality: ProcessedAirQualityData | null;
  isLoading: boolean;
  error: boolean;
  isFromCache: boolean;
  currentLocation: AirQualityLocation;
  availableLocations: AirQualityLocation[];
  setLocation: (locationId: string) => void;
  refetch: () => void;
};

const AirQualityContext = createContext<AirQualityContextType | null>(null);

export function useAirQualityContext() {
  const context = useContext(AirQualityContext);
  if (!context) {
    throw new Error("useAirQualityContext must be used within AirQualityProvider");
  }
  return context;
}

export function AirQualityProvider({ children }: { children: ReactNode }) {
  const [airQuality, setAirQuality] = useState<ProcessedAirQualityData | null>(() => {
    // Natychmiast załaduj z cache przy inicjalizacji
    const savedLocation = getSavedLocation();
    return loadFromCache(savedLocation);
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [isFromCache, setIsFromCache] = useState<boolean>(true); // Zaczynamy od cache
  const [currentLocation, setCurrentLocation] = useState<AirQualityLocation>(getSavedLocation);

  const currentLocationRef = useRef(currentLocation);
  currentLocationRef.current = currentLocation;

  const fetchAirQualityDataRef = useRef<((forceRefresh: boolean) => Promise<void>) | null>(null);

  const fetchAirQualityData = useCallback(async (forceRefresh: boolean = false) => {
    const location = currentLocationRef.current;
    // Pobierz cache string na początku
    const cachedDataString = localStorage.getItem(CACHE_KEY);
    
    // Sprawdź cache tylko jeśli nie wymuszamy odświeżenia
    if (!forceRefresh && cachedDataString) {
      try {
        const cachedData: CachedAirQualityData = JSON.parse(cachedDataString);
        if (isCacheValid(cachedData)) {
          // Użyj danych z cache
          const station = findStationForLocation(cachedData.data, location);
          if (station) {
            setAirQuality(processStationData(station));
            setIsFromCache(true);
            setError(false);
            // Pobierz nowe dane w tle (bez ustawiania isLoading)
            if (fetchAirQualityDataRef.current) {
              fetchAirQualityDataRef.current(true).catch(() => {
                // Ignoruj błędy przy pobieraniu w tle
              });
            }
            return;
          }
        }
      } catch (err) {
        console.error("Error parsing cached air quality data:", err);
      }
    }

    // Pobierz nowe dane z API
    setIsLoading(true);
    setError(false);

    try {
      const apiUrl = getAirQualityApiUrl();
      let responseData: AirQualityApiResponse;
      
      try {
        const { data } = await axios.get<AirQualityApiResponse>(apiUrl, {
          timeout: 15000,
          headers: {
            'Accept': 'application/json',
          },
        });
        responseData = data;
      } catch (axiosError: any) {
        console.warn("Primary proxy request failed, trying alternative CORS proxies:", axiosError.message);
        
        const corsProxies = [
          `https://api.allorigins.win/raw?url=${encodeURIComponent(AIR_QUALITY_API_URL)}`,
          `https://corsproxy.io/?${encodeURIComponent(AIR_QUALITY_API_URL)}`,
        ];
        
        let lastError: Error | null = null;
        let corsResponseData: AirQualityApiResponse | null = null;
        
        for (const corsProxyUrl of corsProxies) {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000);
          
          try {
            const response = await fetch(corsProxyUrl, {
              method: 'GET',
              headers: {
                'Accept': 'application/json',
              },
              signal: controller.signal,
            });
            
            clearTimeout(timeoutId);
            
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            corsResponseData = await response.json();
            break;
          } catch (fetchError: any) {
            clearTimeout(timeoutId);
            lastError = fetchError;
            console.warn(`CORS proxy failed: ${corsProxyUrl}`, fetchError.message);
          }
        }
        
        if (!corsResponseData) {
          throw new Error(`All proxy methods failed. Last error: ${lastError?.message || 'Unknown error'}`);
        }
        
        responseData = corsResponseData;
      }

      if (!responseData || !responseData.smog_data || !Array.isArray(responseData.smog_data) || responseData.smog_data.length === 0) {
        throw new Error("Invalid API response: no data available");
      }

      // Zapisz do cache
      const dataToCache: CachedAirQualityData = {
        data: responseData.smog_data,
        timestamp: Date.now(),
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(dataToCache));

      // Znajdź stację dla wybranej lokalizacji
      const station = findStationForLocation(responseData.smog_data, location);

      if (station) {
        setAirQuality(processStationData(station));
        setIsFromCache(false);
        setError(false);
      } else {
        console.warn(`No station found for location: ${location.name}`);
        setError(true);
      }
    } catch (err: any) {
      console.error("Error fetching air quality data:", err);
      
      // W przypadku błędu, spróbuj użyć cache nawet jeśli wygasł
      let usedCache = false;
      if (cachedDataString) {
        try {
          const cachedData: CachedAirQualityData = JSON.parse(cachedDataString);
          if (cachedData.data && cachedData.data.length > 0) {
            const station = findStationForLocation(cachedData.data, location);
            if (station) {
              setAirQuality(processStationData(station));
              setIsFromCache(true);
              usedCache = true;
              setError(false);
            }
          }
        } catch (parseErr) {
          console.error("Error parsing fallback cache:", parseErr);
        }
      }
      
      if (!usedCache) {
        setError(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  fetchAirQualityDataRef.current = fetchAirQualityData;

  // Pobierz dane przy starcie aplikacji i gdy zmieni się lokalizacja
  useEffect(() => {
    // Najpierw ustaw dane z cache (już ustawione w useState)
    // Następnie pobierz nowe dane w tle
    fetchAirQualityData(false);
  }, [currentLocation.id, fetchAirQualityData]);

  const setLocation = useCallback((locationId: string) => {
    const newLocation = AVAILABLE_LOCATIONS.find(loc => loc.id === locationId);
    if (newLocation && newLocation.id !== currentLocation.id) {
      try {
        localStorage.setItem(SELECTED_LOCATION_KEY, locationId);
      } catch (error) {
        console.warn("Error saving location to localStorage:", error);
      }
      setCurrentLocation(newLocation);
      // Załaduj dane dla nowej lokalizacji z cache natychmiast
      const cachedData = loadFromCache(newLocation);
      if (cachedData) {
        setAirQuality(cachedData);
        setIsFromCache(true);
      }
    }
  }, [currentLocation.id]);

  const refetch = useCallback(() => {
    if (fetchAirQualityDataRef.current) {
      fetchAirQualityDataRef.current(true);
    }
  }, []);

  const value: AirQualityContextType = {
    airQuality,
    isLoading,
    error,
    isFromCache,
    currentLocation,
    availableLocations: AVAILABLE_LOCATIONS,
    setLocation,
    refetch,
  };

  return (
    <AirQualityContext.Provider value={value}>
      {children}
    </AirQualityContext.Provider>
  );
}

